import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import { useCreatePrice, useUpdatePrice } from "../../../stores/MasterTables/therapies.mutations";
import { useTherapiesAdmin } from "../../../stores/MasterTables/therapies.queries";
import type { PriceRange } from "./types";

interface ModalPriceProps {
  open: boolean;
  onClose: () => void;
  editItem: PriceRange | null;
}

const emptyForm = {
  therapyId: "",
  price: "",
  validFrom: "",
  invalidFrom: "",
  lineItem: "",
};

const ModalPrice: React.FC<ModalPriceProps> = ({ open, onClose, editItem }) => {
  const createPrice = useCreatePrice();
  const updatePrice = useUpdatePrice();
  const { data: therapies = [] } = useTherapiesAdmin();

  const [form, setForm] = useState(emptyForm);
  const isEdit = !!editItem;

  useEffect(() => {
    if (open) {
      if (editItem) {
        setForm({
          therapyId: editItem.therapy?.id || "",
          price: editItem.price?.toString() || "",
          validFrom: editItem.validFrom ? editItem.validFrom.substring(0, 10) : "",
          invalidFrom: editItem.invalidFrom ? editItem.invalidFrom.substring(0, 10) : "",
          lineItem: editItem.lineItem ?? "",
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, editItem]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    const payload: any = {
      therapyId: form.therapyId,
      price: parseFloat(form.price),
      validFrom: form.validFrom || "",
      invalidFrom: form.invalidFrom || "",
      lineItem: form.lineItem || undefined,
    };

    try {
      if (isEdit) {
        await updatePrice.mutateAsync({ id: editItem!.id, data: payload });
      } else {
        await createPrice.mutateAsync(payload);
      }
      onClose();
    } catch {
      // error handled in mutation
    }
  };

  const isPending = createPrice.isPending || updatePrice.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isEdit ? "Preis bearbeiten" : "Neuen Preis anlegen"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEdit ? `ID: ${editItem?.id}` : "Wählen Sie ein Heilmittel und definieren Sie den Preis"}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              label="Heilmittel *"
              value={form.therapyId}
              onChange={handleChange("therapyId")}
              fullWidth
              size="small"
              disabled={isEdit}
            >
              {therapies.map((t: any) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.abbreviation} – {t.description}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Preis (€) *"
              type="number"
              value={form.price}
              onChange={handleChange("price")}
              fullWidth
              size="small"
              inputProps={{ step: "0.01", min: "0" }}
              placeholder="z.B. 42.50"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Positionsnummer"
              value={form.lineItem}
              onChange={handleChange("lineItem")}
              fullWidth
              size="small"
              placeholder="z.B. 29950"
              helperText="Leer = übernimmt die Position des Heilmittels"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Gültig ab"
              type="date"
              value={form.validFrom}
              onChange={handleChange("validFrom")}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Gültig bis"
              type="date"
              value={form.invalidFrom}
              onChange={handleChange("invalidFrom")}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} startIcon={<Cancel />} disabled={isPending}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          startIcon={<Save />}
          disabled={isPending || !form.therapyId || !form.price}
        >
          {isPending ? "Speichert…" : isEdit ? "Aktualisieren" : "Anlegen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalPrice;
